import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Flex, Typo, CourseGridCard, Skeleton, SimpleButton, theme, CategoryFilter, ProviderFilter } from '../../ui';
import { useFetchCourseSuggestionsQuery } from '../../features/courses/coursesApi';
import { useGetNetworkProvidersQuery } from '../../features/courses/networkApi';
import { useIsAdmin, useIsIncarnated } from '../../features/auth/hooks';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { selectCurrentStoreHideVat } from '../../features/webshop/globalSlice';
import { addItemToCart, selectCartItemsIds } from '../../features/webshop/cartSlice';
import { TOAST_ERROR } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import { media } from '../../utils/media';
import PageSection from '../../ui/Page/PageSection';

const HeaderSection = styled(Flex)`
  margin-bottom: 24px;
`;

// const NetworkLogo = styled.img`
//   height: 60px;
//   width: auto;
//   object-fit: contain;
// `;

const CoursesGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-gap: 24px;

  ${media.lessThan('tablet')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.lessThan('phablet')} {
    grid-template-columns: 1fr;
  }
`;

const FilterRow = styled(Flex)`
  margin-bottom: 16px;

  ${media.lessThan('phablet')} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CategoryFilterWrapper = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
`;

const SkeletonCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
`;

const MOCK_SUGGESTIONS = [
  {
    id: 'mock-1',
    name: 'Advanced React Patterns',
    description: 'Master advanced React patterns including hooks, context, and performance optimization techniques.',
    ownerName: 'DevMastery',
    ownerSlug: 'devmastery',
    slug: 'advanced-react-patterns',
    public: 1,
    currency: 'EUR',
    price: { gross: 49.99, net: 49.99, discounted: 49.99 },
    coverImage: [{ url: 'https://picsum.photos/400/250?random=30' }],
    categories: [{ slug: 'web-development', name: 'Web Development' }],
    relevanceScore: 95,
  },
  {
    id: 'mock-2',
    name: 'TypeScript for Professionals',
    description: 'Learn TypeScript from basics to advanced type systems, generics, and real-world project patterns.',
    ownerName: 'CodeAcademy Pro',
    ownerSlug: 'codeacademy-pro',
    slug: 'typescript-professionals',
    public: 1,
    currency: 'EUR',
    price: { gross: 59.99, net: 59.99, discounted: 59.99 },
    coverImage: [{ url: 'https://picsum.photos/400/250?random=31' }],
    categories: [{ slug: 'programming', name: 'Programming' }],
    relevanceScore: 88,
  },
  {
    id: 'mock-3',
    name: 'UI/UX Design Principles',
    description: 'Learn the fundamentals of user interface and user experience design for modern applications.',
    ownerName: 'DesignHub',
    ownerSlug: 'designhub',
    slug: 'ui-ux-design-principles',
    public: 1,
    currency: 'EUR',
    price: { gross: 39.99, net: 39.99, discounted: 39.99 },
    coverImage: [{ url: 'https://picsum.photos/400/250?random=32' }],
    categories: [{ slug: 'design', name: 'Design' }],
    relevanceScore: 75,
  },
  {
    id: 'mock-4',
    name: 'Node.js Backend Development',
    description: 'Build scalable backend applications with Node.js, Express, and modern API design patterns.',
    ownerName: 'BackendMasters',
    ownerSlug: 'backendmasters',
    slug: 'nodejs-backend-development',
    public: 1,
    currency: 'EUR',
    price: { gross: 54.99, net: 54.99, discounted: 54.99 },
    coverImage: [{ url: 'https://picsum.photos/400/250?random=33' }],
    categories: [{ slug: 'web-development', name: 'Web Development' }],
    relevanceScore: 92,
  },
  {
    id: 'mock-5',
    name: 'CSS Grid & Flexbox Mastery',
    description: 'Master modern CSS layout techniques with Grid and Flexbox for responsive web design.',
    ownerName: 'CSSExperts',
    ownerSlug: 'cssexperts',
    slug: 'css-grid-flexbox-mastery',
    public: 1,
    currency: 'EUR',
    price: { gross: 34.99, net: 34.99, discounted: 34.99 },
    coverImage: [{ url: 'https://picsum.photos/400/250?random=34' }],
    categories: [{ slug: 'web-development', name: 'Web Development' }],
    relevanceScore: 85,
  },
  {
    id: 'mock-6',
    name: 'Git & GitHub for Teams',
    description: 'Learn version control and collaboration workflows with Git and GitHub for professional teams.',
    ownerName: 'DevOps Academy',
    ownerSlug: 'devops-academy',
    slug: 'git-github-teams',
    public: 1,
    currency: 'EUR',
    price: { gross: 29.99, net: 29.99, discounted: 29.99 },
    coverImage: [{ url: 'https://picsum.photos/400/250?random=35' }],
    categories: [{ slug: 'development-tools', name: 'Development Tools' }],
    relevanceScore: 80,
  },
];

const MOCK_PROVIDERS = [
  { username: 'devmastery', display_name: 'DevMastery' },
  { username: 'codeacademy-pro', display_name: 'CodeAcademy Pro' },
  { username: 'designhub', display_name: 'DesignHub' },
  { username: 'backendmasters', display_name: 'BackendMasters' },
  { username: 'cssexperts', display_name: 'CSSExperts' },
  { username: 'devops-academy', display_name: 'DevOps Academy' },
];

/**
 * NetworkSuggestions Component
 *
 * Displays AI-powered course suggestions from the Coursio Network.
 * Fetches its own data and can be integrated anywhere in the app.
 * Falls back to mock courses when the API returns no results.
 *
 * Features:
 * - Self-contained data fetching via RTK Query
 * - Shows courses using CourseGridCard component
 * - Loading state with skeleton
 * - Responsive grid layout
 * - Mock fallback data for demo/preview
 */
const NetworkSuggestions = ({ searchQuery = '' }) => {

  const { data: suggestions, isLoading, isError } = useFetchCourseSuggestionsQuery();
  const { data: providers } = useGetNetworkProvidersQuery();
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(null);
  const [showMockPreview, setShowMockPreview] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hideVAT = useSelector(selectCurrentStoreHideVat);
  const cartItems = useSelector(selectCartItemsIds);
  const isAdmin = useIsAdmin();
  const isIncarnated = useIsIncarnated();
  const canPreview = isAdmin || isIncarnated;

  const hasRealData = suggestions && suggestions.length > 0;

  const publicSuggestions = useMemo(() => {
    const source = hasRealData ? suggestions : showMockPreview ? MOCK_SUGGESTIONS : [];
    return source.filter((course) => course.public === 1);
  }, [suggestions, hasRealData, showMockPreview]);

  const categories = useMemo(() => {
    if (!publicSuggestions) return [];
    const categoryMap = new Map();
    publicSuggestions.forEach((course) => {
      if (course.categories) {
        course.categories.forEach((cat) => {
          if (!categoryMap.has(cat.slug)) {
            categoryMap.set(cat.slug, cat);
          }
        });
      }
    });
    return Array.from(categoryMap.values());
  }, [publicSuggestions]);

  const filteredSuggestions = useMemo(() => {
    if (!publicSuggestions) return publicSuggestions;
    let filtered = publicSuggestions;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((course) => course.name?.toLowerCase().includes(query));
    }
    if (selectedProviderId !== null) {
      filtered = filtered.filter((course) => (course.ownerSlug || course.owner?.username) === selectedProviderId);
    }
    if (selectedCategorySlug !== null) {
      filtered = filtered.filter((course) => course.categories?.some((cat) => cat.slug === selectedCategorySlug));
    }
    return filtered;
  }, [publicSuggestions, selectedProviderId, selectedCategorySlug, searchQuery]);


  const handleCardClick = useCallback(
    (course) => {
      const isBundle = course.type === 2;
      const ownerSlug = course.ownerSlug || course.owner?.username;
      navigate(`/store/${ownerSlug}/${isBundle ? 'package/' : ''}${course.slug}`, { state: course });
    },
    [navigate],
  );

  const handleBuy = useCallback(
    (item) => {
      if (cartItems.find((el) => el === item.id)) {
        enqueueSnackbar(t('webshop.alreadyInCart'), { variant: TOAST_ERROR });
      } else if (!!cartItems.length) {
        enqueueSnackbar(t('webshop.canOnlyBuyOne'), { variant: TOAST_ERROR });
      } else {
        const ownerSlug = item.ownerSlug || item.owner?.username;
        dispatch(addItemToCart(item));
        navigate(`/store/${ownerSlug}/checkout`);
      }
    },
    [dispatch, cartItems, t, navigate],
  );

  const isEmpty = !isLoading && (!publicSuggestions || publicSuggestions.length === 0);

  const resolvedProviders = hasRealData ? providers : showMockPreview ? MOCK_PROVIDERS : [];
  const showProviderFilter = resolvedProviders && resolvedProviders.length > 1;

  return (
    <PageSection direction='column' align='flex-start' gap='20'>
      <HeaderSection direction='column' align='flex-start' width='100%'>
        <Flex align='center' gap='5'>
          {/* <NetworkLogo src={coursioNetworkLogo} alt='Coursio Network' /> */}
          <Typo.SectionHeader $caps fontWeight='300' color={theme.colors.PRIMARY}>
            Network
          </Typo.SectionHeader>
          <Typo.ContentBigHeader color={theme.colors.COURSIO}>:</Typo.ContentBigHeader>
          <Typo.BigText>{t('coursioNetwork.suggestions')}</Typo.BigText>
        </Flex>
        <Typo.MediumText color={theme.colors.NEU_600} $textAlign='left'>
          {t('coursioNetwork.description')}
        </Typo.MediumText>
      </HeaderSection>

      {isEmpty ? (
        <Flex direction='column' align='center' gap='16' width='100%'>
          <Typo.Text color={theme.colors.NEU_600} $textAlign='center'>
            {t('coursioNetwork.emptySuggestions')}
          </Typo.Text>
          {canPreview && !showMockPreview && (
            <SimpleButton variant='outlined' onClick={() => setShowMockPreview(true)}>
              Show preview with mock data
            </SimpleButton>
          )}
        </Flex>
      ) : (
        <>
          <FilterRow align='center' gap='16' width='100%' justify='space-between'>
            {categories.length > 1 && (
              <CategoryFilterWrapper>
                <CategoryFilter categories={categories} selectedCategorySlug={selectedCategorySlug} onSelect={setSelectedCategorySlug} />
              </CategoryFilterWrapper>
            )}
            {showProviderFilter && <ProviderFilter providers={resolvedProviders} selectedProviderId={selectedProviderId} onSelect={setSelectedProviderId} />}
          </FilterRow>

          {isLoading ? (
            <CoursesGrid>
              {[1, 2, 3].map((i) => (
                <SkeletonCard key={i}>
                  <Skeleton.Base height='200px' width='100%' />
                  <Flex direction='column' gap='8'>
                    <Skeleton.Text width='60%' height='14px' />
                    <Skeleton.Text width='90%' height='18px' />
                    <Skeleton.Text width='100%' height='14px' />
                    <Skeleton.Base height='40px' width='100%' />
                  </Flex>
                </SkeletonCard>
              ))}
            </CoursesGrid>
          ) : filteredSuggestions?.length === 0 ? (
            <Typo.Text color={theme.colors.NEU_600} $textAlign='center'>
              {t('coursioNetwork.noResults')}
            </Typo.Text>
          ) : (
            <CoursesGrid>
              {filteredSuggestions?.map((course) => (
                <CourseGridCard
                  key={course.id}
                  courseInfo={course}
                  onCardClick={handleCardClick}
                  onBuy={handleBuy}
                  hideVAT={hideVAT}
                  // matchPercentage={course.relevanceScore ?? getMockMatchPercentage(course.id)}
                  matchPercentage={course.relevanceScore}
                  buyLabel={t('coursioNetwork.buyNow')}
                />
              ))}
            </CoursesGrid>
          )}
        </>
      )}
    </PageSection>
  );
};

export default NetworkSuggestions;
